/**
 * NumberInput Component
 * 
 * Specialized input component for numeric values with validation and formatting.
 * 
 * Following SOLID principles:
 * - Single Responsibility: Only handles numeric input with validation
 * - Open/Closed: Extensible through props without modification
 * - Interface Segregation: Clean, focused interface
 * 
 * Features:
 * - Real-time input validation
 * - Min/max value constraints
 * - Unit display
 * - Disabled state support
 * - Focus/blur handling
 */

import React, { useState, useRef, useEffect } from "react";

/**
 * Props interface for the NumberInput component
 * 
 * Responsibility: Defines the contract for numeric input configuration
 * Used by: NumberInput component
 */
interface NumberInputProps {
  /** Display label for the input field */
  label: string;
  
  /** Current numeric value */
  value: number;
  
  /** Callback function when value changes */
  onChange: (value: number) => void;
  
  /** Placeholder text for empty input */
  placeholder?: string;
  
  /** Whether the input is disabled */
  disabled?: boolean;
  
  /** Unit of measurement to display (e.g., "L", "s", "min") */
  unit?: string;
  
  /** Minimum allowed value */
  min?: number;
  
  /** Maximum allowed value */
  max?: number;
  
  /** Step increment for numeric input */
  step?: number;
}

/**
 * NumberInput component for numeric values with validation
 * 
 * Responsibility: Provides a controlled numeric input with validation and formatting
 * Used by: HomePage, various configuration forms
 * 
 * This component handles:
 * - Numeric input validation
 * - Min/max value constraints
 * - Input formatting
 * - Focus/blur state management
 * - Unit display
 */
export const NumberInput: React.FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  placeholder = "0",
  disabled = false,
  unit,
  min,
  max,
  step = 1
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  /** Current input field value as string (for controlled input) */
  const [inputValue, setInputValue] = useState("");
  
  /** Whether the input field is currently focused */
  // const [isFocused, setIsFocused] = useState(false); // Unused
  
  /** Reference to track the last valid value for change detection */
  const lastValueRef = useRef(value);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Format a number for display in the input field
   * 
   * @param numValue - The number to format
   * @returns Formatted string representation
   */
  const formatNumber = (numValue: number): string => {
    if (numValue === undefined || numValue === null) return "";
    if (numValue === 0) return "0";
    return numValue.toString();
  };

  /**
   * Parse a string input into a valid number
   * 
   * Handles validation, cleaning, and constraint application.
   * 
   * @param numString - The string to parse
   * @returns Parsed number or 0 if invalid
   */
  const parseNumber = (numString: string): number => {
    if (!numString || numString.trim() === '') {
      return 0;
    }

    // Clean input - allow optional leading minus, numbers and decimal point
    const cleanInput = numString.replace(/[^0-9.-]/g, '')
      .replace(/(?!^)-/g, ''); // only allow '-' at start
    
    // Handle multiple decimal points
    const parts = cleanInput.split('.');
    if (parts.length > 2) {
      return 0; // Invalid format
    }

    const numericValue = parseFloat(cleanInput);
    if (isNaN(numericValue)) {
      return 0;
    }

    // Apply min/max constraints
    if (min !== undefined && numericValue < min) return min;
    if (max !== undefined && numericValue > max) return max;

    return numericValue;
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Update input value when external value changes
   * 
   * This ensures the input field stays synchronized with the parent component's state.
   */
  useEffect(() => {
    if (lastValueRef.current !== value) {
      setInputValue(formatNumber(value));
      lastValueRef.current = value;
    }
  }, [value]);

  /**
   * Initialize input value on component mount
   * 
   * Sets the initial display value when the component first renders.
   */
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setInputValue(formatNumber(value));
      lastValueRef.current = value;
    }
  }, []);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle input field change events
   * 
   * Updates the local input value but doesn't validate or notify parent yet.
   * This allows users to type freely without immediate validation.
   * 
   * @param e - Input change event
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = e.target.value;
    setInputValue(newInputValue);
    
    // Don't update the parent while typing - let user type freely
    // Only validate when they leave the field
  };

  /**
   * Handle input field focus events
   * 
   * Sets the focused state for styling purposes.
   */
  const handleFocus = () => {
    // setIsFocused(true); // Unused
  };

  /**
   * Handle input field blur events
   * 
   * Performs validation and notifies parent of changes when user leaves the field.
   * This is where the actual value parsing and constraint application happens.
   */
  const handleBlur = () => {
    // setIsFocused(false); // Unused
    
    // Only validate and format when user leaves the field
    const parsedValue = parseNumber(inputValue);
    
    if (!isNaN(parsedValue)) {
      const formattedValue = formatNumber(parsedValue);
      setInputValue(formattedValue);
      onChange(parsedValue);
      lastValueRef.current = parsedValue;
    } else if (inputValue.trim() !== '') {
      // If user typed something but it's invalid, keep the input as-is
      // Don't clear it, just don't update the parent
      setInputValue(inputValue);
    } else {
      // Only clear if the input is completely empty
      setInputValue("");
      onChange(0);
      lastValueRef.current = 0;
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-2">
      {/* Input label with optional unit */}
      <label className="block text-sm font-medium text-slate-300">
        {label}
        {unit && <span className="text-slate-400 ml-1">({unit})</span>}
      </label>
      
      {/* Input field container */}
      <div className="relative">
        <input
          type="number"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          className="w-full px-3 py-2 bg-slate-800 rounded-xl border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
};
